import { mapParsedFiltersToUrlParams, AI_DERIVED_PARAM_KEYS } from './searchRouting';
import { tagParamFromChips } from './listingTagSearch';
import { parseSelectFilterOptions } from './search';
import {
  routeSearch,
  getVisitorSessionId,
  setPendingRoute,
  flushAbandonedSearch,
} from './searchTelemetry';

// ── Recent searches (mobile search overlay) ─────────────────────────────────
// localStorage-backed list of the user's last submitted queries, most-recent
// first, case-insensitive deduped, capped at RECENT_SEARCHES_MAX. All access
// is SSR-guarded and try/catch-wrapped (private-mode browsers throw) — recents
// are strictly best-effort and must never break a search.

const RECENT_SEARCHES_KEY = 'tphRecentSearches';
const RECENT_SEARCHES_MAX = 5;

export const getRecentSearches = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string' && s.trim()) : [];
  } catch (e) {
    return [];
  }
};

export const recordRecentSearch = rawQuery => {
  const q = typeof rawQuery === 'string' ? rawQuery.trim() : '';
  if (!q || typeof window === 'undefined') return;
  try {
    const rest = getRecentSearches().filter(s => s.toLowerCase() !== q.toLowerCase());
    const next = [q, ...rest].slice(0, RECENT_SEARCHES_MAX);
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch (e) {
    // localStorage unavailable — skip silently.
  }
};

export const removeRecentSearch = query => {
  if (typeof window === 'undefined') return;
  try {
    const q = typeof query === 'string' ? query : '';
    const next = getRecentSearches().filter(s => s.toLowerCase() !== q.toLowerCase());
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next));
  } catch (e) {
    // localStorage unavailable — skip silently.
  }
};

/**
 * Story 8.14 (ID-6, ID-9) — the Sharetribe `keywords` string for a search
 * whose structured filters mapped: the non-null `search_term` of every
 * `searchable` unmatched chip, deduplicated case-insensitively, space-joined.
 * Never `label` (it can carry a "sire:" prefix) and never `source_phrase` (it
 * carries the buyer's typos and cross notation). A chip from a pre-V1_6 parse
 * has neither field and contributes nothing.
 *
 * @param {Array<Object>} chips `unmatched_keywords` from /search/route
 * @returns {string|null} the keyword string, or null when there is none
 */
export const searchKeywordsFromChips = chips => {
  if (!Array.isArray(chips)) {
    return null;
  }
  const terms = [];
  chips.forEach(chip => {
    const term =
      chip && chip.class === 'searchable' && typeof chip.search_term === 'string'
        ? chip.search_term.trim()
        : '';
    if (term && !terms.some(t => t.toLowerCase() === term.toLowerCase())) {
      terms.push(term);
    }
  });
  return terms.length > 0 ? terms.join(' ') : null;
};

const BREAKAWAY = 'breakaway';
const wordTokens = text =>
  `${text || ''}`
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * Story 8.18 (ID-8, breakaway option C) — the parser maps "breakaway" to the
 * Roping discipline and keeps no chip, so the word is lost and a breakaway
 * search returns all of Roping. Keep it as a keyword, but ONLY when Roping is
 * the one discipline: Sharetribe ANDs `keywords` with `pub_*` filters, so on
 * "Barrel and breakaway" it would drop every barrel horse that never says it.
 *
 * @param {string} rawQuery the query the buyer typed
 * @param {Object} searchParams the params about to be pushed
 * @returns {string|undefined} the keywords to send, or the existing value
 */
const keywordsWithBreakaway = (rawQuery, searchParams) => {
  const { keywords } = searchParams;
  const disciplines = searchParams.pub_disciplines
    ? parseSelectFilterOptions(`${searchParams.pub_disciplines}`)
    : [];
  const ropingOnly = disciplines.length === 1 && disciplines[0] === 'roping';
  if (!ropingOnly || !wordTokens(rawQuery).includes(BREAKAWAY)) {
    return keywords;
  }
  if (wordTokens(keywords).includes(BREAKAWAY)) {
    return keywords;
  }
  return keywords ? `${keywords} ${BREAKAWAY}` : BREAKAWAY;
};

/**
 * Story 8.2 (AC9) — route-at-submit orchestration for a keyword search.
 *
 * Calls tph-api /search/route BEFORE navigating, then pushes either a keyword
 * URL (`?keywords=`) or an AI-parsed URL (`?aiKeywords=<raw>&<mapped filters>`).
 * The route result is stashed (sessionStorage) so SearchPage can issue the
 * single /search/record after results render. Fail-open: if the route call
 * fails, fall back to a plain keyword search so search never breaks.
 *
 * Shared by the Topbar desktop search and the homepage hero search so both
 * entry points behave identically.
 *
 * @param {Object} args
 * @param {string} args.rawQuery the trimmed query the user typed
 * @param {Object} args.currentSearchParams existing search params to preserve/replace
 * @param {Object} args.currentUser current user entity (for telemetry user_uuid)
 * @param {Object} args.config marketplace configuration
 * @param {Function} args.pushSearch (searchParams) => void — navigates to /s with params
 */
export const routeAndSubmitKeywordSearch = async ({
  rawQuery,
  currentSearchParams,
  currentUser,
  config,
  pushSearch,
}) => {
  const userUuid = currentUser?.id?.uuid;

  // A new query REPLACES the prior one: drop stale keyword/aiKeywords state and
  // any previously AI-derived filter params so they don't stack.
  const base = { ...currentSearchParams };
  delete base.keywords;
  delete base.aiKeywords;
  AI_DERIVED_PARAM_KEYS.forEach(k => {
    delete base[k];
  });

  let routeResult = null;
  try {
    routeResult = await routeSearch(rawQuery, { userUuid });
  } catch (e) {
    routeResult = null; // fail-open → plain keyword search
  }

  let searchParams;
  if (routeResult && routeResult.routed_to === 'ai_iso') {
    const mapped = mapParsedFiltersToUrlParams(routeResult.parsed_filters, config);
    const hasStructured = Object.keys(mapped).length > 0;
    // aiKeywords carries the raw query verbatim; stringify() encodes it once.
    searchParams = { ...base, ...mapped, aiKeywords: rawQuery };
    // Keyword fallback (8.9 AC3/AC5): when NO structured filter mapped, the raw
    // text must still drive a title/description keyword match instead of an
    // unconstrained whole-catalog dump.
    //
    // Story 8.14 (ID-6, ID-9): when a structured filter IS present, the RAW
    // query is still never sent — Sharetribe ANDs keywords with pub_* filters,
    // and "barrel horse over 50k" as keywords would demand words no listing
    // carries (the over-constraint hazard). What IS sent is only what the
    // parser could not map: the `search_term` of each `searchable` chip (a sire
    // name, "1D", "papers"). Filler ("ISO") and price_intent ("cheap") chips
    // carry no term and are never sent. No term at all → no `keywords` key.
    //
    // Story 8.16 (ID-5): with NO structured filter the same rule applies —
    // send the chips' search_term, not the raw sentence ("All horses sired by
    // Aint Seen Nothin Yet" → keywords=Aint Seen Nothin Yet, since Sharetribe
    // ORs every word of a multi-word keyword string). The raw query is the
    // LAST resort only, when no chip carries a term (no chips, a pre-V1_6
    // parse, chips that are all filler / price_intent, or searchable chips
    // whose search_term is null — the "with papers" chip): no filter AND no
    // keyword would be the unconstrained whole-catalog dump 8.9 AC3 forbids.
    if (!hasStructured) {
      const chipKeywords = searchKeywordsFromChips(routeResult.unmatched_keywords);
      searchParams.keywords = chipKeywords ? chipKeywords : rawQuery;
    } else {
      const unmatchedKeywords = searchKeywordsFromChips(routeResult.unmatched_keywords);
      if (unmatchedKeywords) {
        searchParams.keywords = unmatchedKeywords;
      }
    }

    // Story 8.18 (ID-1, ID-7) — the listing-tag filter is ADDED after the
    // keyword choice above, never instead of it: a tag comes only from a chip
    // whose search_term is already in `keywords`. Dropping the keyword loses
    // the relevance order and sinks the horses buyers graded Perfect.
    Object.assign(searchParams, tagParamFromChips(routeResult.unmatched_keywords));
    const keywords = keywordsWithBreakaway(rawQuery, searchParams);
    if (keywords) {
      searchParams.keywords = keywords;
    }
  } else {
    searchParams = { ...base, keywords: rawQuery };
  }

  // Story 8.11 (AC2b) — PRIMARY ABANDON FLUSH.
  //
  // This is the single choke point for every keyword/AI submit in the app (the
  // Topbar, the homepage hero and the ISO bar all funnel through here), and the
  // stash below is the only writer of the pending-route key. So a pending route
  // still present at THIS line belongs to an earlier search that was submitted
  // and then abandoned before its results landed — the user gave up and typed
  // something else. Flush it as `unmeasured` before it is overwritten, or the
  // search event is lost entirely (§2.4: write with a status, never suppress).
  //
  // Placed AFTER the `await routeSearch` deliberately: if the earlier search's
  // results land during that await, the completion path gets to claim it first
  // and records a genuine `measured` row. This flush only ever sees what that
  // path did not take.
  //
  // Runs unconditionally — including when `routeResult` is null (fail-open) and
  // no new route will be stashed, since the earlier route is abandoned either
  // way. Fire-and-forget: it never awaits, so `pushSearch` below is not delayed.
  flushAbandonedSearch();

  // Stash the route result so the SearchPage post-render effect can perform the
  // single /search/record once result_count + latency are known.
  if (routeResult) {
    setPendingRoute({
      raw_query: rawQuery,
      visitor_session_id: getVisitorSessionId(),
      user_uuid: userUuid || null,
      routed_to: routeResult.routed_to,
      routing_reason: routeResult.routing_reason,
      route_context: routeResult.route_context,
      ts: Date.now(),
    });
  }

  pushSearch(searchParams);

  // Submit succeeded (navigation pushed) — remember the raw query for the
  // mobile search overlay's RECENT list.
  recordRecentSearch(rawQuery);
};
